<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells>
        <app-input
          @input.native="onSearch"
          v-model="search"
          class="input--300"
          type="search"
          placeholder="Search by place name"
          name="search"
        />
        <app-filters
          v-model="filter_checked"
          :filters="filters"
          name="filterTransfers"
          @change="onChangeFilter"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__name><div class="table-default__left">Name</div></template>
        <template #h__short_name><div class="table-default__left">Short name</div></template>
        <template #h__area><div class="table-default__left">Area</div></template>
        <template #h__region><div class="table-default__left">Region</div></template>
        <template #h__territory><div class="table-default__left">Territory</div></template>
        <template #name="{ row }"><div class="table-default__left">{{ row.name }}</div></template>
        <template #short_name="{ row }"><div class="table-default__left">{{ row.short_name }}</div></template>
        <template #type="{ row }">{{ row.type ? row.type.name : '-' }}</template>
        <template #is_fly_in_out="{ row }">
          <span
            class="status-tag"
            :style="{ 'backgroundColor': row.is_fly_in_out ? '#77b017' : '#e85c20' }"
          >
            {{ row.is_fly_in_out ? 'Yes' : 'No' }}
          </span>
        </template>
        <template #area="{ row }">
          <div class="table-default__left">
            <template v-if="row.area">{{ row.area.name }}</template>
            <template v-else>-</template>
          </div>
        </template>
        <template #region="{ row }">
          <div class="table-default__left">
            <template v-if="row.area">{{ row.area.region.name }}</template>
            <template v-else>-</template>
          </div>
        </template>
        <template #territory="{ row }">
          <div class="table-default__left">
            <template v-if="row.area">{{ row.area.region.territory.name }}</template>
            <template v-else>-</template>
          </div>
        </template>
        <template #place_id="{ row }">
          <span
              class="status-tag"
              :style="{ 'backgroundColor': row.place_id ? '#77b017' : '#e85c20' }"
          >
            {{ row.place_id ? 'Yes' : 'No' }}
          </span>
        </template>
        <template #checked="{ row }">
          <span
              class="status-tag"
              :style="{ 'backgroundColor': row.checked ? '#77b017' : '#e85c20' }"
          >
            {{ row.checked ? 'Yes' : 'No' }}
          </span>
        </template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onOpenEditPopup(row)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
              :clickHandler="onClickPagination"
              v-if="pagination.pages > 1"
              v-model="pagination.page"
              :page-count="pagination.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
        name="location-form"
        :width="400"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('location-form')" class="popup__close" type="button"></button>
        <div class="popup__title">Edit location</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Name" label-for="location_name" required>
              <app-input
                  v-model="form.name"
                  @change.native="$v.form.name.$touch()"
                  :error="$v.form.name.$error"
                  id="location_name"
                  placeholder="Location name"
                  name="location_name"
              />
              <template #error>
                <div v-if="$v.form.name.$dirty && !$v.form.name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Short name" label-for="short_name" required>
              <app-input
                  v-model="form.short_name"
                  @change.native="$v.form.short_name.$touch()"
                  :error="$v.form.short_name.$error"
                  id="short_name"
                  placeholder="Location short name"
                  name="short_name"
              />
              <template #error>
                <div v-if="$v.form.short_name.$dirty && !$v.form.short_name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Type">
              <app-select
                v-model="form.type"
                :options="location_types_options"
                placeholder="Type"
              />
            </app-form-group>
            <app-form-group label="Area">
              <app-select
                v-model="form.area"
                :options="location_areas_options"
                placeholder="Type"
              />
            </app-form-group>
            <app-form-group label="Checked">
              <app-switcher v-model="form.checked" :readonly="!form.type || !form.area" />
            </app-form-group>
            <app-form-group label="Fly in/out">
              <app-switcher v-model="form.is_fly_in_out" />
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">Save location</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { getLocationAreas, getLocations, getLocationTypes, patchLocation } from '@/http'
import debounce from 'lodash/debounce'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'Locations',
  data() {
    return {
      is_loaded: false,
      table_preloader: false,
      search: '',
      filter_checked: '',
      filters: [
        { value: '', label: 'All' },
        { value: 'true', label: 'Checked' },
        { value: 'false', label: 'Not checked' },
      ],
      table: {
        items: [],
        columns: ['name', 'short_name', 'type', 'is_fly_in_out', 'area', 'region', 'territory', 'place_id', 'checked', 'controls'],
        options: {
          headings: {
            'name': 'Name',
            'short_name': 'Short name',
            'type': 'Type',
            'is_fly_in_out': 'Fly in/out',
            'area': 'Area',
            'region': 'Region',
            'territory': 'Territory',
            'place_id': 'Google ID',
            'checked': 'Checked',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      location_types_options: [],
      form: {},
      location_areas_options: []
    }
  },
  validations: {
    form: {
      name: { required },
      short_name: { required },
    }
  },
  created() {
    this.fetchItems()
    getLocationTypes().then(res => { this.location_types_options = res.data })
    getLocationAreas().then(res => { this.location_areas_options = res.data })
  },
  methods: {
    fetchItems() {
      getLocations({ page: this.pagination.page, page_size: 40, search: this.search, checked: this.filter_checked })
        .then(res => {
          this.table.items = res.data.results
          this.pagination.pages = res.data.pages
          this.pagination.count = res.data.count
          this.is_loaded = true
          this.table_preloader = false
        })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      patchLocation(this.form.id, this.normalizeForm())
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Location saved',
          })
          this.fetchItems()
          this.$modal.hide('location-form')
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    normalizeForm() {
      const normalizedForm = { ...this.form }

      if (normalizedForm.type) normalizedForm.type = normalizedForm.type.code
      if (normalizedForm.area) normalizedForm.area = normalizedForm.area.id

      return normalizedForm
    },
    onOpenEditPopup(location) {
      this.$v.form.$reset()
      this.form = location
      this.$modal.show('location-form')
    },
    onChangeFilter() {
      this.table_preloader = true
      this.search = ''
      this.fetchItems()
    },
    onSearch() {
      this.searchLocations(this)
    },
    searchLocations: debounce(vm => {
      vm.fetchItems()
    }, 350),
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
  }
}
</script>
